import React, { useState, useCallback, useMemo } from 'react';
import { v4 } from 'uuid';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core';
import { deepPure } from '~/util';
import { useMeContext } from '~/modules/me/useMeContext';
import { useResourcingChartToolbarContext } from '~/modules/projects/resourcing-plan/hooks';
import {
  actionsWidth,
  itemWidth
} from '~/modules/common/charts/timeline/calculations';
import { ConditionalWrapper } from '~/modules/common/components';
import {
  StyledChartRangeItem2,
  STYLED_CHART_RANGE_ITEM_VARIANT
} from '~/modules/common/charts/timeline/components';
import { useDialogState } from '~/modules/common/hooks';
import { useChartDisplayPeriods } from '~/modules/resourcing/common/chart/hooks';
import {
  useProjectResourceAllocationsByIds,
  useUserScheduleBackgroundImage
} from '~/modules/resourcing/common/hooks';
import { useGetTaskResourceUserAllocationsSummaryForUser } from '~/modules/tasks/hooks';
import { ReleaseResourceAllocationType, ResourceCostModeType } from '~/types';
import { useProjectContext } from '~/modules/resourcing/common/contexts';
import { getUserCostRateDetails } from '~/modules/projects/resourcing-plan/ResourceRequestChart/components/ResourceAllocationChart/hooks';
import { ResourceAllocationChartLeftItem2 } from '../ResourceAllocationChartLeftItem';
import { ResourceAllocationChartRightItem } from '../ResourceAllocationChartRightItem';
import { ResourceAllocationChartTimeLines } from './ResourceAllocationChartTimeLines';
import { UserTaskAssignments } from './components';
import { getUserOverDistributionDetails } from './components/UserTaskAssignments/hooks/useUserOverDistributionDetails';
import UserAllocationsSummaryContextProvider from './UserAllocationsSummaryContextProvider';

const useChartRangeItemStyles = makeStyles(() => ({
  itemContainer: {
    alignItems: 'normal',
    zIndex: 4
  },
  totalContainer: {
    zIndex: 4
  }
}));

const useModel1Styles = makeStyles(() => ({
  itemContainer: {
    alignItems: 'normal',
    zIndex: 4,
    display: 'flex',
    justifyContent: 'center'
  }
}));

const useChartRowStyles = makeStyles(theme => ({
  userRows: {
    display: 'flex',
    flexDirection: 'row',
    backgroundColor: theme.palette.background.paper
  }
}));

const getAllocationIdsForUser = allocatedUser =>
  allocatedUser.roles.reduce(
    (acc, role) => [
      ...acc,
      ...(role.resourceAllocationReference || []).map(ref => ref.id)
    ],
    []
  );

export const getAllocationByAllocationId = entries =>
  (entries || []).reduce(
    (acc, entry) => ({
      ...acc,
      [entry.id]: entry
    }),
    {}
  );

export const ResourceAllocationChartRow2 = ({
  allocatedUser,
  autofocusedUserId,
  currentPage,
  handleEditResourceAllocation,
  handleRemoveResourceAllocation,
  loadingUserScheduleDetails,
  loadingOtherProjectAllocationsSummary,
  userActuals,
  isActualsLoading,
  projectId,
  resourceCostMode,
  resetResourceAllocationChart,
  setAutofocusedUserId,
  setSnackBarState,
  userScheduleDetails,
  onUserRowExpansionPanelClick,
  isUserRowExpanded,
  otherProjectAllocationsSummary,
  roleScheduleAsOfDate
}) => {
  const classes = useChartRowStyles();
  const me = useMeContext();
  const {
    featureFlags: { isPsaRmpTaskAllocation1Enabled }
  } = me;

  const chartRangeItemClasses = useChartRangeItemStyles();
  const model1Classes = useModel1Styles();

  const chartRangeItemClassesFFOn = useMemo(
    () => ({
      ...chartRangeItemClasses,
      ...model1Classes
    }),
    [chartRangeItemClasses, model1Classes]
  );

  const {
    openDialog,
    closeDialog,
    open: userAllocationDialogOpen
  } = useDialogState();
  const [newResourceUser, setNewResourceUser] = useState(null);
  const onUserChange = useCallback(
    newUser => {
      if (!newUser) return;
      setNewResourceUser(newUser);
      openDialog();
    },
    [openDialog]
  );
  const {
    projectObjectPermissions: { canViewTasks }
  } = useProjectContext();
  const {
    scale,
    chartDates,
    dateRange,
    isResourceActualModeEnabled,
    isRmp2026Q1Enhancements,
    shouldUseScheduleBasedBackgroundImage
  } = useResourcingChartToolbarContext();
  const [{ start: chartStartDate }] = chartDates;
  const [userAssignmentsRowKey, setUserAssignmentsRowKey] = useState(
    allocatedUser.user.id
  );
  const onReleaseResourceAllocationSuccess = useCallback(
    ({ releaseType }) => {
      if (
        releaseType === ReleaseResourceAllocationType.Asof ||
        isResourceActualModeEnabled
      ) {
        isPsaRmpTaskAllocation1Enabled && setUserAssignmentsRowKey(v4());
      }
    },
    [isPsaRmpTaskAllocation1Enabled, isResourceActualModeEnabled]
  );

  const allocationIds = getAllocationIdsForUser(allocatedUser);
  const {
    loadingAllocations,
    resourceAllocations
  } = useProjectResourceAllocationsByIds({
    allocationIds,
    resourceCostMode,
    skip: !allocationIds.length
  });

  const {
    loading: userTaskAllocationsSummaryLoading,
    taskResourceUserAllocationsSummary
  } = useGetTaskResourceUserAllocationsSummaryForUser({
    userId: allocatedUser.user.id,
    projectId,
    dateRange,
    skip: !isPsaRmpTaskAllocation1Enabled || !canViewTasks
  });

  const resourceAllocationsById = getAllocationByAllocationId(
    resourceAllocations
  );

  const chartDisplayPeriods = useChartDisplayPeriods({
    chartDisplayDateRange: dateRange,
    scale
  });

  const {
    isUserTaskAllocationsOverDistributed,
    userOverDistributedPeriodsMap
  } = useMemo(
    () =>
      !loadingAllocations && !userTaskAllocationsSummaryLoading
        ? getUserOverDistributionDetails({
            chartDisplayDateRange: dateRange,
            chartDisplayPeriods,
            isPsaRmpTaskAllocation1Enabled,
            resourceAllocationScheduleRules:
              resourceAllocations[0]?.scheduleRules,
            userTaskAllocationsSummaryScheduleRules:
              taskResourceUserAllocationsSummary?.scheduleRules
          })
        : {},
    [
      chartDisplayPeriods,
      dateRange,
      isPsaRmpTaskAllocation1Enabled,
      loadingAllocations,
      resourceAllocations,
      taskResourceUserAllocationsSummary?.scheduleRules,
      userTaskAllocationsSummaryLoading
    ]
  );

  const userCostRateDetails = getUserCostRateDetails({
    resourceAllocation: resourceAllocations[0]
  });

  const {
    userScheduleBackgroundImage: backgroundImageOverride
  } = useUserScheduleBackgroundImage({
    userUri: allocatedUser.user.id,
    dateRange,
    scale,
    skip: !shouldUseScheduleBasedBackgroundImage
  });

  return (
    <div key={allocatedUser.user.uri} className={classes.userRow}>
      <StyledChartRangeItem2
        backgroundImageOverride={backgroundImageOverride}
        classes={
          isPsaRmpTaskAllocation1Enabled
            ? chartRangeItemClassesFFOn
            : chartRangeItemClasses
        }
        scale={scale}
        scaleItemCount={chartDates.length}
        chartStartDate={chartStartDate}
        leftComponentWidth={actionsWidth + itemWidth * 2}
        variant={
          isRmp2026Q1Enhancements
            ? STYLED_CHART_RANGE_ITEM_VARIANT.BLANK
            : undefined
        }
      >
        <ResourceAllocationChartLeftItem2
          projectId={projectId}
          allocatedUser={allocatedUser}
          currentPage={currentPage}
          onUserChange={onUserChange}
          handleRemoveResourceAllocation={handleRemoveResourceAllocation}
          setSnackBarState={setSnackBarState}
          autofocusedUserId={autofocusedUserId}
          setAutofocusedUserId={setAutofocusedUserId}
          onUserRowExpansionPanelClick={onUserRowExpansionPanelClick}
          isUserRowExpanded={isUserRowExpanded}
          onReleaseResourceAllocationSuccess={
            onReleaseResourceAllocationSuccess
          }
          roleScheduleAsOfDate={roleScheduleAsOfDate}
          isUserTaskAllocationsOverDistributed={
            isUserTaskAllocationsOverDistributed
          }
          resetResourceAllocationChart={resetResourceAllocationChart}
        />
        <ConditionalWrapper
          condition={isPsaRmpTaskAllocation1Enabled}
          WrapperComponent={UserAllocationsSummaryContextProvider}
          userTaskAllocationsSummaryScheduleRules={
            taskResourceUserAllocationsSummary?.scheduleRules
          }
        >
          <ResourceAllocationChartTimeLines
            loadingAllocations={loadingAllocations}
            loadingUserScheduleDetails={loadingUserScheduleDetails}
            loadingOtherProjectAllocationsSummary={
              loadingOtherProjectAllocationsSummary
            }
            userActuals={userActuals}
            isActualsLoading={isActualsLoading}
            resourceAllocationsById={resourceAllocationsById}
            userScheduleDetails={userScheduleDetails}
            otherProjectAllocationsSummary={otherProjectAllocationsSummary}
            handleRemoveResourceAllocation={handleRemoveResourceAllocation}
            setSnackBarState={setSnackBarState}
            allocatedUser={allocatedUser}
            newResourceUser={newResourceUser}
            closeUserAllocationDialog={closeDialog}
            userAllocationDialogOpen={userAllocationDialogOpen}
            handleEditResourceAllocation={handleEditResourceAllocation}
            projectId={projectId}
            chartDisplayPeriods={chartDisplayPeriods}
          />
        </ConditionalWrapper>

        <ResourceAllocationChartRightItem
          allocatedUser={allocatedUser}
          loadingAllocations={loadingAllocations}
          resourceAllocationsById={resourceAllocationsById}
        />
      </StyledChartRangeItem2>
      {isUserRowExpanded && (
        <UserTaskAssignments
          backgroundImageOverride={backgroundImageOverride}
          key={userAssignmentsRowKey}
          user={allocatedUser.user}
          userCostRateDetails={userCostRateDetails}
          userScheduleDetails={userScheduleDetails}
          resourceAllocationScheduleRules={
            resourceAllocations[0]?.scheduleRules
          }
          taskResourceUserAllocationsSummary={
            taskResourceUserAllocationsSummary
          }
          userTaskAllocationsSummaryLoading={userTaskAllocationsSummaryLoading}
          loadingResourceAllocation={loadingAllocations}
          loadingUserScheduleDetails={loadingUserScheduleDetails}
          userOverDistributedPeriodsMap={userOverDistributedPeriodsMap}
          chartDisplayPeriods={chartDisplayPeriods}
          resetResourceAllocationChart={resetResourceAllocationChart}
          handleRemoveResourceAllocation={handleRemoveResourceAllocation}
        />
      )}
    </div>
  );
};

ResourceAllocationChartRow2.propTypes = {
  allocatedUser: PropTypes.object.isRequired,
  projectId: PropTypes.string,
  currentPage: PropTypes.number,
  handleRemoveResourceAllocation: PropTypes.func,
  handleEditResourceAllocation: PropTypes.func,
  loadingUserScheduleDetails: PropTypes.bool,
  loadingOtherProjectAllocationsSummary: PropTypes.bool,
  otherProjectAllocationsSummary: PropTypes.object,
  isActualsLoading: PropTypes.bool,
  userActuals: PropTypes.array,
  resourceCostMode: PropTypes.oneOf(Object.values(ResourceCostModeType)),
  setSnackBarState: PropTypes.func,
  autofocusedUserId: PropTypes.string,
  setAutofocusedUserId: PropTypes.func,
  userScheduleDetails: PropTypes.object.isRequired,
  onUserRowExpansionPanelClick: PropTypes.func.isRequired,
  isUserRowExpanded: PropTypes.bool.isRequired,
  resetResourceAllocationChart: PropTypes.func.isRequired,
  roleScheduleAsOfDate: PropTypes.object
};

export default deepPure(ResourceAllocationChartRow2);
